import { MSG } from 'asteroids-shared/protocol'
import ClientSocket from './net/ClientSocket.js'

const buildRoomSocketUrl = (wsPath, name, roomCode = '') => {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  const url = new URL(`${proto}//${window.location.host}${wsPath}`)
  url.searchParams.set('name', name)
  if (roomCode) url.searchParams.set('roomCode', roomCode)
  return url.toString()
}

export default class AppRoomSocket {
  #socket = null
  #ignoreDisconnect = false

  constructor() {
    this.handlers = {
      onConnected: null,
      onWelcome: null,
      onLobbyState: null,
      onGameStart: null,
      onGameOver: null,
      onLobbyBell: null,
      onKicked: null,
      onError: null,
      onDisconnect: null
    }
  }

  setHandlers(handlers) {
    this.handlers = { ...this.handlers, ...handlers }
  }

  getSocket() {
    return this.#socket
  }

  close(suppressDisconnect = false) {
    if (!this.#socket) return
    this.#ignoreDisconnect = suppressDisconnect
    const socket = this.#socket
    this.#socket = null
    socket.close()
  }

  attachSocket(socket) {
    this.#socket = socket

    for (const [type, key, after] of [
      [MSG.WELCOME, 'onWelcome'],
      [MSG.LOBBY_STATE, 'onLobbyState'],
      [MSG.GAME_START, 'onGameStart'],
      [MSG.GAME_OVER, 'onGameOver'],
      [MSG.LOBBY_BELL, 'onLobbyBell'],
      [MSG.ERROR, 'onError'],
      [MSG.KICKED, 'onKicked', () => socket.close()]
    ]) {
      socket.on(type, message => {
        this.handlers[key]?.(message)
        after?.()
      })
    }

    socket.on('disconnect', () => {
      if (this.#socket === socket) this.#socket = null
      const ignored = this.#ignoreDisconnect
      this.#ignoreDisconnect = false
      this.handlers.onDisconnect?.({ ignored })
    })
  }

  async connectToRoom({ name, wsPath, roomCode = '' }) {
    this.close(true)

    const socket = new ClientSocket()
    this.attachSocket(socket)

    try {
      await socket.connect(buildRoomSocketUrl(wsPath, name, roomCode))
      this.handlers.onConnected?.(socket)
    } catch (error) {
      this.#ignoreDisconnect = true
      socket.close()
      if (this.#socket === socket) this.#socket = null
      throw error
    }
  }

  setReady(ready) {
    this.#socket?.setReady(ready)
  }

  kickVote(targetId, vote) {
    this.#socket?.kickVote(targetId, vote)
  }

  leaveRoom() {
    this.#socket?.leaveRoom()
  }

  castVote(vote) {
    this.#socket?.castVote(vote)
  }

  returnLobby() {
    this.#socket?.returnLobby()
  }

  ringLobbyBell() {
    this.#socket?.ringLobbyBell()
  }
}
